import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Phone, Search } from 'lucide-react';
import styles from './ClientHome.module.css';
import { Images } from '@assets/images/Images';
import bookClosedIcon from '@/ui/assets/icons/bookClosedIcon.svg';
import CategoryButton from "@/ui/components/client/CategoryButton/CategoryButton.js";
import ProductList from "@/ui/components/client/ProductList/ProductList";
import { Categories } from "@/data/categories.js";
import { getProducts } from '@services/clientProductFirebaseService';
import { getOrCreateReviewerId } from '@/utils/reviewerId';
import { getVisibleClientCategories } from '@/utils/productCategories';
import { ROUTES } from '@/config/routes';

let cachedProducts = null;

function ClientHome() {
    const navigate = useNavigate();
    const [reviewerId] = useState(() => getOrCreateReviewerId());
    const [activeCategory, setActiveCategory] = useState(() => {
        return sessionStorage.getItem('clientActiveCategory') || Categories[0].id;
    });
    const [searchTerm, setSearchTerm] = useState(() => {
        return sessionStorage.getItem('clientSearchTerm') || "";
    });

    const [products, setProducts] = useState(() => {
        if (cachedProducts) return cachedProducts;
        try {
            const localData = localStorage.getItem('catalog_products_cache');
            if (localData) {
                const parsed = JSON.parse(localData);
                cachedProducts = parsed;
                return parsed;
            }
        } catch (e) {
            console.error("Error leyendo localStorage:", e);
        }
        return [];
    });

    const contentRef = useRef(null);

    useEffect(() => {
        const fetchAndValidateProducts = async () => {
            try {
                const freshProducts = await getProducts();

                const currentDataStr = JSON.stringify(products);
                const freshDataStr = JSON.stringify(freshProducts);

                if (currentDataStr !== freshDataStr) {
                    cachedProducts = freshProducts;

                    localStorage.setItem('catalog_products_cache', freshDataStr);

                    setProducts(freshProducts);
                }
            } catch (error) {
                console.error("Error revalidando productos:", error);
            }
        };

        fetchAndValidateProducts();
    }, [products]);

    const visibleCategories = useMemo(
        () => getVisibleClientCategories(products),
        [products]
    );

    useEffect(() => {
        if (visibleCategories.length === 0) return;

        const isActiveVisible = visibleCategories.some(
            (cat) => cat.id === activeCategory
        );

        if (!isActiveVisible) {
            setActiveCategory(visibleCategories[0].id);
        }
    }, [visibleCategories, activeCategory]);

    useEffect(() => {
        sessionStorage.setItem('clientActiveCategory', activeCategory);
    }, [activeCategory]);

    useEffect(() => {
        sessionStorage.setItem('clientSearchTerm', searchTerm);
    }, [searchTerm]);

    useEffect(() => {
        if (contentRef.current && products.length > 0) {
            const savedScroll = sessionStorage.getItem('clientScrollTop');
            if (savedScroll) {
                const scrollTimeout = setTimeout(() => {
                    if (contentRef.current) {
                        contentRef.current.scrollTop = Number(savedScroll);
                    }
                }, 100);
                return () => clearTimeout(scrollTimeout);
            }
        }
    }, [products]);

    return (
        <div className={styles.appContainer}>
            <header className={styles.header}>
                <div className={styles.brandSection}>
                    <img src={Images.logoDulceComoVos} alt="Logo Dulce como vos" className={styles.logoPlaceholder} />
                    <div className={styles.brandInfo}>
                        <h1 className={styles.brandInfoTitle}>Dulce como vos</h1>
                        <p className={styles.brandInfoSubtitle}>Café &amp; Helado</p>
                        <span className={styles.brandInfoPhone}>
                            <Phone size={26} color="#7C625E" fill="#7C625E" strokeWidth={0} />
                            +57 315 624 6231
                        </span>
                    </div>
                </div>
            </header>

            <div
                className={styles.overlapContent}
                ref={contentRef}
                onScroll={(e) => {
                    sessionStorage.setItem('clientScrollTop', String(e.currentTarget.scrollTop));
                }}
            >
                <div className={styles.searchContainer}>
                    <div className={styles.searchBox}>
                        <span className={styles.searchIcon}>
                            <Search size={26} color="#AC6E39" strokeWidth={3} />
                        </span>
                        <input
                            className={styles.searchInput}
                            type="text"
                            placeholder="Buscar producto..."
                            value={searchTerm}
                            onChange={(e) => {
                                setSearchTerm(e.target.value);
                                sessionStorage.setItem('clientScrollTop', '0');
                                if (contentRef.current) {
                                    contentRef.current.scrollTop = 0;
                                }
                            }}
                        />
                    </div>
                </div>

                <div className={styles.categories}>
                    {visibleCategories.map((category) => (
                        <CategoryButton
                            key={category.id}
                            category={category}
                            isActive={activeCategory === category.id}
                            onClick={() => {
                                setActiveCategory(category.id);
                                sessionStorage.setItem('clientScrollTop', '0');
                                if (contentRef.current) {
                                    contentRef.current.scrollTop = 0;
                                }
                            }}
                        />
                    ))}
                </div>

                <ProductList
                    activeCategory={activeCategory}
                    searchTerm={searchTerm}
                    products={products}
                    reviewerId={reviewerId}
                />
            </div>

            <button
                type="button"
                className={styles.floatingActionButton}
                aria-label="Abrir libro"
                onClick={() => navigate(ROUTES.CLIENT_MENU)}
            >
                <img src={bookClosedIcon} alt="" className={styles.floatingActionIcon} />
            </button>
        </div>
    );
}

export default ClientHome;